import apiRoutes from '@constants/apiRoutes';
import baseApi from '@services/api';
import {
  InviteUserPayload,
  UpdateUserAccessPayload,
  ClientUsersListResponse,
  Clients,
  RevokeUserAccessPayload,
  PendingAccessRequest,
  AcceptAccessRequestPayload,
  RejectAccessRequestPayload,
} from './types/client-user-management';
import { AdminClientCompanyDashboardsResponse } from './types/clientUsers';
import { ApiResponse } from './types/common';

const clientUserManagement = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCompanyList: builder.query<ApiResponse<Clients[]>, void>({
      query: () => apiRoutes.clientsList,
    }),
    getCompanyUsers: builder.query<
      ApiResponse<ClientUsersListResponse>,
      { clientCode: string; queryString?: string }
    >({
      query: ({ clientCode, queryString }) =>
        apiRoutes.clientUserManagementUsers(clientCode, queryString),
    }),
    getClientDashboards: builder.query<ApiResponse<AdminClientCompanyDashboardsResponse>, string>({
      query: (clientCode) => apiRoutes.clientUserManagementDashboards(clientCode),
    }),
    inviteUserByClient: builder.mutation<ApiResponse<string>, InviteUserPayload>({
      query: ({ clientCode, ...payload }) => ({
        url: apiRoutes.inviteUser(clientCode),
        method: 'POST',
        body: payload,
      }),
    }),
    updateUserAccess: builder.mutation<ApiResponse<string>, UpdateUserAccessPayload>({
      query: ({ clientCode, membershipGrantId, ...payload }) => ({
        url: apiRoutes.updateUserAccess(clientCode, membershipGrantId),
        method: 'PUT',
        body: payload,
      }),
    }),
    revokeUserAccess: builder.mutation<ApiResponse<string>, RevokeUserAccessPayload>({
      query: ({ clientCode, membershipGrantId }) => ({
        url: apiRoutes.revokeUserAccess(clientCode, membershipGrantId),
        method: 'DELETE',
      }),
    }),
    getPendingAccessRequests: builder.query<ApiResponse<PendingAccessRequest[]>, string>({
      query: (clientCode) => apiRoutes.pendingAccessRequests(clientCode),
    }),
    acceptAccessRequest: builder.mutation<ApiResponse<string>, AcceptAccessRequestPayload>({
      query: ({ clientCode, requestId, ...payload }) => ({
        url: apiRoutes.acceptAccessRequest(clientCode, requestId),
        method: 'POST',
        body: payload,
      }),
    }),
    rejectAccessRequest: builder.mutation<ApiResponse<string>, RejectAccessRequestPayload>({
      query: ({ clientCode, requestId }) => ({
        url: apiRoutes.rejectAccessRequest(clientCode, requestId),
        method: 'POST',
      }),
    }),
  }),
});

export const {
  useInviteUserByClientMutation,
  useGetCompanyListQuery,
  useLazyGetCompanyListQuery,
  useGetCompanyUsersQuery,
  useLazyGetCompanyUsersQuery,
  useGetClientDashboardsQuery,
  useLazyGetClientDashboardsQuery,
  useUpdateUserAccessMutation,
  useRevokeUserAccessMutation,
  useLazyGetPendingAccessRequestsQuery,
  useAcceptAccessRequestMutation,
  useRejectAccessRequestMutation,
} = clientUserManagement;
